import { useMemo } from "react";
import { useNavigate } from "react-router";
import type { EChartsOption } from "echarts";
import { Activity, AlertTriangle, Blocks, Bot, CheckCircle2, Cpu, Database, HardDrive, Layers, PlugZap, Radio, Rocket, Server, TrendingDown, Workflow, XCircle } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { PlatformIcon, tileClasses } from "@ncp/assistant-sdk";
import { formatDateTime, formatNumber, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EChart, EmptyState, PageHeader, Skeleton, StatCard, Timeline, chartPalette, cn } from "@ncp/ui";
import { useHelpTopic } from "../app/console-context";
import { usePlatformOverview, type ComponentStatus, type OverviewWorkspace, type PlatformOverview } from "../features/overview/api";

const COMPONENT_ICONS = { api: Server, database: Database, storage: HardDrive, jobs: Layers, realtime: Radio, llm: Cpu } as const;

function statusTone(s: ComponentStatus): "success" | "warning" | "danger" | "outline" {
  return s === "Healthy" ? "success" : s === "Degraded" ? "warning" : s === "Down" ? "danger" : "outline";
}

export function OverviewPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const q = usePlatformOverview();
  const d = q.data;
  const workspaces = d?.workspaces ?? [];

  useHelpTopic({
    title: t("admin:overview.title"),
    docsAnchor: "overview",
    body: (
      <>
        <p>{t("admin:overview.subtitle")}</p>
        <p>{t("admin:overview.healthHint")}</p>
        <p>{t("admin:overview.runningCasesHint")}</p>
      </>
    ),
  });

  const spendOption = useMemo<EChartsOption | null>(() => {
    if (!d?.spend.daily.length) return null;
    return {
      grid: { left: 40, right: 12, top: 12, bottom: 24 },
      xAxis: { type: "category", data: d.spend.daily.map((x) => x.day.slice(5)), axisLabel: { fontSize: 10 } },
      yAxis: { type: "value", axisLabel: { fontSize: 10, formatter: (v: number) => `$${v}` } },
      series: [{ type: "bar", data: d.spend.daily.map((x) => Math.round(x.usd * 100) / 100), itemStyle: { color: chartPalette[0], borderRadius: [4, 4, 0, 0] } }],
      tooltip: { trigger: "axis", valueFormatter: (v) => `$${Number(v).toFixed(2)}` },
    };
  }, [d]);

  const down = d?.components.filter((c) => c.status === "Down").length ?? 0;
  const degraded = d?.components.filter((c) => c.status === "Degraded").length ?? 0;

  return (
    <div>
      <PageHeader
        eyebrow={t("admin:app.title")}
        title={t("admin:overview.title")}
        description={t("admin:overview.subtitle")}
        meta={d ? <span className="text-xs text-muted-foreground">{t("admin:overview.refreshed", { when: formatRelative(d.generatedAt, locale) })} · {t("admin:app.version", { version: d.version })}</span> : null}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/releases")}>
              <Rocket /> {t("admin:nav.releases")}
            </Button>
            <Button onClick={() => navigate("/workspaces")}>{t("admin:nav.workspaces")}</Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t("admin:overview.health")}
          value={d ? d.components.filter((c) => c.status === "Healthy").length : 0}
          unit={d ? `/ ${d.components.length}` : undefined}
          icon={down ? XCircle : degraded ? AlertTriangle : CheckCircle2}
          tone={down ? "camel" : degraded ? "camel" : "gold"}
          footer={q.isLoading ? "…" : down ? t("admin:overview.status.Down") : degraded ? t("admin:overview.status.Degraded") : t("admin:overview.status.Healthy")}
        />
        <StatCard label={t("admin:overview.agents")} value={d?.agents.length ?? 0} icon={Bot} tone="sea" footer={d ? `${d.agents.reduce((s, a) => s + a.runs24h, 0)} ${t("admin:overview.agent.runs").toLowerCase()} · 24h` : undefined} />
        <StatCard label={t("admin:overview.connectors")} value={d?.connectors.length ?? 0} icon={PlugZap} tone={d?.connectors.some((c) => c.breaker === "Open") ? "camel" : "neutral"} footer={d ? `${d.connectors.filter((c) => c.breaker === "Open").length} ${t("admin:overview.breaker.Open").toLowerCase()}` : undefined} />
        <StatCard label={t("admin:overview.approvals")} value={d?.pendingChangeSets.length ?? 0} icon={Rocket} tone={d?.pendingChangeSets.length ? "camel" : "neutral"} footer={<button type="button" className="text-aegold-800 hover:underline" onClick={() => navigate("/releases")}>{t("admin:overview.viewAll")}</button>} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Activity className="size-4 text-aegold-700" /> {t("admin:overview.health")}</CardTitle>
            <CardDescription>{t("admin:overview.healthHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-28" /> : (
              <div className="grid gap-3 sm:grid-cols-3">
                {d?.components.map((c) => {
                  const Icon = COMPONENT_ICONS[c.key];
                  return (
                    <div key={c.key} className="flex items-start gap-3 rounded-xl border border-sand-200 p-3">
                      <span className={cn("grid size-9 shrink-0 place-items-center rounded-lg", c.status === "Healthy" ? "bg-success-50 text-success-700" : c.status === "Degraded" ? "bg-warning-50 text-warning-700" : c.status === "Down" ? "bg-danger-50 text-danger-700" : "bg-sand-100 text-sand-600")}>
                        <Icon className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{t(`admin:overview.components.${c.key}`)}</p>
                          <Badge size="sm" variant={statusTone(c.status)} dot>{t(`admin:overview.status.${c.status}`)}</Badge>
                        </div>
                        <p className="truncate text-xs text-muted-foreground">{c.detail ?? (c.latencyMs != null ? `${c.latencyMs} ms` : "")}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {d?.queues.length ? (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-sand-600">{t("admin:overview.queues")}</p>
                <table className="w-full text-xs">
                  <thead className="text-sand-600"><tr>{(["name", "enqueued", "processing", "failed", "succeeded24h"] as const).map((k) => <th key={k} className={cn("py-1 font-medium", k === "name" ? "text-start" : "text-end")}>{t(`admin:overview.queue.${k}`)}</th>)}</tr></thead>
                  <tbody>
                    {d.queues.map((qq) => (
                      <tr key={qq.name} className="border-t border-sand-100">
                        <td className="py-1.5 font-mono">{qq.name}</td>
                        <td className="py-1.5 text-end">{qq.enqueued}</td>
                        <td className="py-1.5 text-end">{qq.processing}</td>
                        <td className={cn("py-1.5 text-end", qq.failed && "font-semibold text-danger-700")}>{qq.failed}</td>
                        <td className="py-1.5 text-end">{qq.succeeded24h}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Rocket className="size-4 text-aegold-700" /> {t("admin:overview.approvals")}</CardTitle>
            <CardDescription>{t("admin:overview.approvalsHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-28" /> : !d?.pendingChangeSets.length ? (
              <EmptyState icon={CheckCircle2} title={t("admin:overview.noApprovals")} className="py-8" />
            ) : (
              <ul className="space-y-2">
                {d.pendingChangeSets.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => navigate(`/releases/${c.id}`)} className="w-full rounded-xl border border-sand-200 p-3 text-start transition-colors hover:border-aegold-300 hover:bg-aegold-50/40">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium">{c.title}</p>
                        <Badge size="sm" variant="outline">{c.items}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{c.requestedBy} · {formatRelative(c.requestedAt, locale)}{c.effectiveFrom ? ` · ${formatDateTime(c.effectiveFrom, locale)}` : ""}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {can("platform.workspaces.read") && (q.isLoading || workspaces.length > 0) ? (
        <Card className="mt-6">
          <CardHeader className="flex-row items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Blocks className="size-4 text-aegold-700" /> {t("admin:overview.workspaces")}</CardTitle>
              <CardDescription>{t("admin:overview.workspacesHint")}</CardDescription>
            </div>
            <Button size="sm" variant="ghost" onClick={() => navigate("/workspaces")}>{t("admin:overview.viewAll")}</Button>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-36" /> : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {workspaces.map((w) => <WorkspaceCard key={w.key} w={w} onOpen={() => navigate(`/workspaces/${encodeURIComponent(w.key)}`)} />)}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Bot className="size-4 text-aegold-700" /> {t("admin:overview.agents")}</CardTitle>
            <CardDescription>{t("admin:overview.agentsHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-48" /> : (
              <div className="max-h-80 overflow-auto scrollbar-thin">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white text-sand-600">
                    <tr>
                      <th className="py-1 text-start font-medium">{t("admin:overview.agent.name")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.agent.runs")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.agent.success")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.agent.p95")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.agent.lastFailure")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d?.agents.map((a) => (
                      <tr key={a.key} className="cursor-pointer border-t border-sand-100 hover:bg-sand-50" onClick={() => navigate(`/agents/${a.key}`)}>
                        <td className="py-1.5"><span className="font-medium">{a.name}</span> {a.configurable ? <Badge size="sm" variant="ai" className="ms-1">cfg</Badge> : null}</td>
                        <td className="py-1.5 text-end">{a.runs24h}</td>
                        <td className={cn("py-1.5 text-end", a.successRate != null && a.successRate < 90 && "font-semibold text-warning-700")}>{a.successRate == null ? "—" : `${a.successRate}%`}</td>
                        <td className="py-1.5 text-end">{a.p95Ms == null ? "—" : `${(a.p95Ms / 1000).toFixed(1)} s`}</td>
                        <td className="py-1.5 text-end text-muted-foreground">{a.lastFailureAt ? formatRelative(a.lastFailureAt, locale) : t("admin:overview.agent.never")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><PlugZap className="size-4 text-aegold-700" /> {t("admin:overview.connectors")}</CardTitle>
            <CardDescription>{t("admin:overview.connectorsHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-48" /> : (
              <div className="max-h-80 overflow-auto scrollbar-thin">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white text-sand-600">
                    <tr>
                      <th className="py-1 text-start font-medium">{t("admin:overview.connector.name")}</th>
                      <th className="py-1 text-start font-medium">{t("admin:overview.connector.health")}</th>
                      <th className="py-1 text-start font-medium">{t("admin:overview.connector.breaker")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.connector.latency")}</th>
                      <th className="py-1 text-end font-medium">{t("admin:overview.connector.lastCall")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d?.connectors.map((c) => (
                      <tr key={c.key} className="cursor-pointer border-t border-sand-100 hover:bg-sand-50" onClick={() => navigate(`/integrations?key=${c.key}`)}>
                        <td className="py-1.5 font-medium">{c.name}</td>
                        <td className="py-1.5"><Badge size="sm" variant={c.health === "Healthy" ? "success" : c.health === "Degraded" ? "warning" : "danger"} dot>{c.health}</Badge></td>
                        <td className="py-1.5"><Badge size="sm" variant={c.breaker === "Closed" ? "outline" : "danger"}>{t(`admin:overview.breaker.${c.breaker}`)}</Badge></td>
                        <td className="py-1.5 text-end">{c.avgLatencyMs == null ? "—" : `${c.avgLatencyMs} ms`}</td>
                        <td className="py-1.5 text-end text-muted-foreground">{c.lastCallAt ? formatRelative(c.lastCallAt, locale) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Cpu className="size-4 text-aegold-700" /> {t("admin:overview.providers")}</CardTitle>
            <CardDescription>{t("admin:overview.providersHint")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {q.isLoading ? <Skeleton className="h-24" /> : d?.providers.map((p) => (
              <button type="button" key={p.key} onClick={() => navigate("/governance")} className="flex w-full items-center justify-between gap-2 rounded-xl border border-sand-200 p-3 text-start hover:border-aegold-300">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.kind} · {p.residency}</p>
                </div>
                <div className="flex items-center gap-1">
                  {p.offline ? <Badge size="sm" variant="warning">offline</Badge> : null}
                  <Badge size="sm" variant={p.active ? "success" : "outline"}>{p.routes}</Badge>
                </div>
              </button>
            ))}
            {spendOption ? (
              <div className="pt-3">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-sand-600">{t("admin:overview.spend")} · ${d?.spend.last30dUsd.toFixed(2)}</p>
                <EChart option={spendOption} className="h-28" />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Workflow className="size-4 text-aegold-700" /> {t("admin:overview.runningCases")}</CardTitle>
            <CardDescription>{t("admin:overview.runningCasesHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-40" /> : <RunningCases rows={d?.runningCases ?? []} onOpen={(k) => navigate(`/workflows?key=${k}`)} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TrendingDown className="size-4 text-aegold-700" /> {t("admin:overview.recentChanges")}</CardTitle>
            <CardDescription>{t("admin:overview.recentChangesHint")}</CardDescription>
          </CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-40" /> : !d?.recentChanges.length ? <EmptyState title={t("admin:common.empty")} className="py-8" /> : (
              <Timeline
                items={d.recentChanges.slice(0, 8).map((c) => ({
                  id: c.id,
                  title: <span className="text-sm">{c.action}</span>,
                  actor: c.actor,
                  detail: <span className="text-xs text-muted-foreground">{c.target}{c.detail ? ` — ${c.detail}` : ""}</span>,
                  time: formatRelative(c.at, locale),
                  kind: "system" as const,
                }))}
              />
            )}
            {d?.drift.length ? (
              <div className="mt-4 rounded-xl border border-warning-200 bg-warning-50/60 p-3 text-xs">
                <p className="font-semibold text-warning-800">{t("admin:overview.drift")}</p>
                {d.drift.map((x) => <p key={x.dataset + x.at}>{x.dataset}: {x.from}% → {x.to}% · {formatRelative(x.at, locale)}</p>)}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Adoption, spend-vs-budget and review backlog of one workspace; the budget bar turns amber past 80% and red when exhausted. */
function WorkspaceCard({ w, onOpen }: { w: OverviewWorkspace; onOpen: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = locale === "ar" && w.nameAr ? w.nameAr : w.name;
  const ratio = w.monthlyBudgetUsd > 0 ? Math.min(1, w.spendMonthUsd / w.monthlyBudgetUsd) : 0;
  const barTone = ratio >= 1 ? "bg-danger-500" : ratio >= 0.8 ? "bg-warning-500" : "bg-aegold-500";
  const usd = (v: number) => formatNumber(v, locale, { style: "currency", currency: "USD", maximumFractionDigits: v >= 100 ? 0 : 2 });
  return (
    <button type="button" onClick={onOpen} className={cn("rounded-2xl border border-sand-200 bg-white/80 p-4 text-start transition-colors hover:border-aegold-300 hover:bg-aegold-50/30", !w.enabled && "opacity-60")}>
      <div className="flex items-start gap-3">
        <span className={tileClasses(w.color, "size-10")}><PlatformIcon name={w.icon} fallback={Blocks} className="size-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="truncate font-semibold">{name}</h3>
            {w.pendingProposals ? <Badge size="sm" variant="warning">{t("admin:overview.workspace.pending", { count: w.pendingProposals })}</Badge> : null}
            {!w.enabled ? <Badge size="sm" variant="default">{t("admin:common.disabled")}</Badge> : null}
          </div>
          <p className="text-xs text-muted-foreground">{t("admin:overview.workspace.summary", { assistants: w.assistants, members: w.members })}</p>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
        {[
          { label: t("admin:overview.workspace.threads7d"), main: w.threadsThisWeek, sub: t("admin:overview.workspace.threadsTotal", { count: w.threads }) },
          { label: t("admin:overview.workspace.activeUsers"), main: w.activeUsers7d, sub: t("admin:overview.workspace.membersTotal", { count: w.members }) },
          { label: t("admin:overview.workspace.runs"), main: w.delegatedRunsMonth, sub: t("admin:overview.workspace.artifacts", { count: w.artifacts }) },
        ].map(({ label, main, sub }) => (
          <div key={label} className="rounded-xl bg-sand-50 p-2">
            <dt className="text-[10px] uppercase tracking-wider text-sand-600">{label}</dt>
            <dd className="text-base font-semibold tabular">{formatNumber(main, locale)}</dd>
            <dd className="text-[10px] text-muted-foreground">{sub}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t("admin:overview.workspace.spend")}</span>
          <span className={cn("font-medium tabular", ratio >= 1 && "text-danger-700", ratio >= 0.8 && ratio < 1 && "text-warning-700")}>
            {usd(w.spendMonthUsd)}{w.monthlyBudgetUsd > 0 ? ` / ${usd(w.monthlyBudgetUsd)}` : ""}
          </span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-sand-100" role="progressbar" aria-valuenow={Math.round(ratio * 100)} aria-valuemin={0} aria-valuemax={100}>
          <span className={cn("block h-full rounded-full", barTone)} style={{ width: `${w.monthlyBudgetUsd > 0 ? ratio * 100 : w.spendMonthUsd > 0 ? 100 : 0}%` }} />
        </div>
      </div>
    </button>
  );
}

function RunningCases({ rows, onOpen }: { rows: PlatformOverview["runningCases"]; onOpen: (key: string) => void }) {
  const { t } = useTranslation();
  if (!rows.length) return <EmptyState title={t("admin:common.empty")} className="py-8" />;
  const byWorkflow = rows.reduce<Record<string, PlatformOverview["runningCases"]>>((acc, r) => ((acc[r.workflowKey] ??= []).push(r), acc), {});
  return (
    <div className="space-y-3">
      {Object.entries(byWorkflow).map(([key, versions]) => {
        const total = versions.reduce((s, v) => s + v.cases, 0);
        return (
          <button type="button" key={key} onClick={() => onOpen(key)} className="block w-full text-start">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{versions[0].workflowName}</span>
              <span className="text-muted-foreground">{total}</span>
            </div>
            <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-sand-100">
              {versions.map((v, i) => (
                <span key={v.version} title={`v${v.version}: ${v.cases}`} className={cn("h-full", v.isActive ? "bg-aegold-500" : "bg-sand-400")} style={{ width: `${total ? (v.cases / total) * 100 : 0}%`, opacity: 1 - i * 0.15 }} />
              ))}
            </div>
            <p className="mt-0.5 text-[10px] text-muted-foreground">{versions.map((v) => `v${v.version}${v.isActive ? "*" : ""}: ${v.cases}`).join(" · ")}</p>
          </button>
        );
      })}
    </div>
  );
}
